import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { MerchantCategoryPicker } from "../components/MerchantCategoryPicker";
import { OfficeLocationPickerMap } from "../components/OfficeLocationPickerMap";
import { AuthShell } from "../components/AuthShell";
import {
  listMerchantBusinessCategories,
  registerMerchant,
  getMerchantToken,
  requestMerchantEmailOtp,
  verifyMerchantEmailOtp,
} from "../services/api";
import { validateMerchantPortalPassword } from "../utils/merchantPortalPassword";
import { createValidatedImageObjectUrl } from "../utils/safeMerchantImageFile";
import { validateMerchantCompliancePdfFile } from "../utils/safeMerchantPdfFile";
import { friendlyError } from "../utils/friendlyError";
import { t } from "../i18n";

const labelStyle = { display: "block", marginBottom: 4, fontSize: 13, color: "#1b1530", fontWeight: 600 };
const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  backgroundColor: "#ffffff",
  border: "1px solid #e6e1f0",
  color: "#1b1530",
  borderRadius: 12,
};

const INITIAL = {
  name: "",
  contactEmail: "",
  password: "",
  confirmPassword: "",
  tin: "",
  officeAddress: "",
  officeLat: "",
  officeLng: "",
  contactPerson: "",
  contactPhone: "",
  registrationNumber: "",
};

export function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profilePreviewUrl, setProfilePreviewUrl] = useState(null);
  const [tinPdfFile, setTinPdfFile] = useState(null);
  const [businessRegistrationPdfFile, setBusinessRegistrationPdfFile] = useState(null);
  const [businessLicensePdfFile, setBusinessLicensePdfFile] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState(() => new Set());
  const [verifiedEmail, setVerifiedEmail] = useState(null);
  const [otpSending, setOtpSending] = useState(false);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [otpNotice, setOtpNotice] = useState("");

  const normalizedEmail = form.contactEmail.trim().toLowerCase();
  const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);
  const emailIsVerified = verifiedEmail != null && verifiedEmail === normalizedEmail;

  useEffect(() => {
    let cancelled = false;
    listMerchantBusinessCategories()
      .then((rows) => {
        if (!cancelled) setCategoryOptions(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setCategoryOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (profilePreviewUrl) URL.revokeObjectURL(profilePreviewUrl);
    };
  }, [profilePreviewUrl]);

  useEffect(() => {
    document.title = `${t("reg.title")} · ${t("title.suffix")}`;
  }, []);

  if (getMerchantToken()) {
    return <Navigate to="/" replace />;
  }

  function update(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSendOtp() {
    if (!emailLooksValid || otpSending) return;
    const resending = otpModalOpen;
    setOtpSending(true);
    setOtpError("");
    if (!resending) setError("");
    setOtpNotice("");
    try {
      await requestMerchantEmailOtp(normalizedEmail);
      setOtpValue("");
      setOtpModalOpen(true);
      setOtpNotice(t("reg.code_sent", { email: normalizedEmail }));
    } catch (err) {
      const msg = friendlyError(err, t("reg.send_code_failed"));
      if (resending) setOtpError(msg);
      else setError(msg);
    } finally {
      setOtpSending(false);
    }
  }

  function closeOtpModal() {
    setOtpModalOpen(false);
    setOtpError("");
    setOtpNotice("");
  }

  async function handleConfirmOtp(e) {
    e.preventDefault();
    if (otpVerifying) return;
    setOtpVerifying(true);
    setOtpError("");
    try {
      await verifyMerchantEmailOtp(normalizedEmail, otpValue);
      setVerifiedEmail(normalizedEmail);
      setOtpModalOpen(false);
      setOtpNotice("");
    } catch (err) {
      setOtpError(friendlyError(err, t("reg.invalid_code")));
    } finally {
      setOtpVerifying(false);
    }
  }

  function toggleCategory(code) {
    const c = String(code).toUpperCase();
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  }

  function parseLat(v) {
    const raw = String(v).trim();
    if (!raw) return { error: t("reg.lat_required") };
    const n = Number(raw);
    if (!Number.isFinite(n) || n < -90 || n > 90) {
      return { error: t("reg.lat_range") };
    }
    return { value: n };
  }

  function parseLng(v) {
    const raw = String(v).trim();
    if (!raw) return { error: t("reg.lng_required") };
    const n = Number(raw);
    if (!Number.isFinite(n) || n < -180 || n > 180) {
      return { error: t("reg.lng_range") };
    }
    return { value: n };
  }

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) {
      setError(t("reg.err_name"));
      return;
    }
    if (!form.contactEmail.trim()) {
      setError(t("login.email_required"));
      return;
    }
    if (!emailIsVerified) {
      setError(t("reg.err_verify_email"));
      return;
    }
    const pwErr = validateMerchantPortalPassword(form.password);
    if (pwErr) {
      setError(pwErr);
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError(t("forgot.mismatch"));
      return;
    }
    if (!form.tin.trim()) {
      setError(t("reg.err_tin"));
      return;
    }
    if (!form.registrationNumber.trim()) {
      setError(t("reg.err_registration"));
      return;
    }
    if (selectedCategories.size === 0) {
      setError(t("reg.err_category"));
      return;
    }
    const latRes = parseLat(form.officeLat);
    if (latRes.error) {
      setError(latRes.error);
      return;
    }
    const lngRes = parseLng(form.officeLng);
    if (lngRes.error) {
      setError(lngRes.error);
      return;
    }
    if (!form.officeAddress.trim()) {
      setError(t("reg.err_address"));
      return;
    }
    if (!form.contactPerson.trim()) {
      setError(t("reg.err_contact_person"));
      return;
    }
    if (!form.contactPhone.trim()) {
      setError(t("reg.err_contact_phone"));
      return;
    }
    if (!(profileImageFile instanceof File)) {
      setError(t("reg.err_photo"));
      return;
    }
    if (!(tinPdfFile instanceof File)) {
      setError(t("reg.err_tin_pdf"));
      return;
    }
    if (!(businessRegistrationPdfFile instanceof File)) {
      setError(t("reg.err_registration_pdf"));
      return;
    }
    if (!(businessLicensePdfFile instanceof File)) {
      setError(t("reg.err_license_pdf"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        contactEmail: form.contactEmail.trim().toLowerCase(),
        password: form.password,
        tin: form.tin.trim(),
        registrationNumber: form.registrationNumber.trim(),
        officeAddress: form.officeAddress.trim(),
        officeLatitude: latRes.value,
        officeLongitude: lngRes.value,
        contactPerson: form.contactPerson.trim(),
        contactPhone: form.contactPhone.trim(),
        businessCategories: Array.from(selectedCategories),
      };
      await registerMerchant(
        payload,
        profileImageFile,
        tinPdfFile,
        businessRegistrationPdfFile,
        businessLicensePdfFile
      );
      navigate("/login", {
        replace: true,
        state: { registeredEmail: form.contactEmail.trim().toLowerCase() },
      });
    } catch (err) {
      const msg = err?.response?.data?.message;
      const detail = err?.response?.data?.errors ?? err?.response?.data?.details;
      const detailStr = Array.isArray(detail)
        ? detail.map((d) => (typeof d === "object" && d?.message ? d.message : String(d))).join(" ")
        : null;
      setError(
        (typeof msg === "string" && msg) ||
          detailStr ||
          friendlyError(err, t("reg.failed"))
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <AuthShell wide eyebrow={t("reg.eyebrow")} title={t("reg.title")}>
        <p className="auth-card__lead">
          {t("reg.lead")}
        </p>
        <form onSubmit={submit}>
          <div style={{ display: "grid", gap: 14 }}>
            <label style={labelStyle}>
              {t("reg.business_name")}
              <input
                required
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder={t("reg.business_name_placeholder")}
                style={{ ...inputStyle, marginTop: 6 }}
              />
            </label>
            <label style={labelStyle}>
              {t("reg.contact_email")}
              <div style={{ display: "flex", gap: 8, marginTop: 6, alignItems: "center" }}>
                <input
                  required
                  type="email"
                  value={form.contactEmail}
                  onChange={(e) => update("contactEmail", e.target.value)}
                  placeholder={t("reg.email_placeholder")}
                  disabled={emailIsVerified}
                  style={{
                    ...inputStyle,
                    marginTop: 0,
                    flex: 1,
                    opacity: emailIsVerified ? 0.7 : 1,
                    cursor: emailIsVerified ? "not-allowed" : "text",
                  }}
                />
                {emailIsVerified ? (
                  <span
                    style={{
                      whiteSpace: "nowrap",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#2f7a57",
                      padding: "8px 10px",
                    }}
                  >
                    ✓ {t("store.verified")}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={!emailLooksValid || otpSending}
                    style={{
                      whiteSpace: "nowrap",
                      padding: "8px 12px",
                      borderRadius: 12,
                      border: "1px solid #e6e1f0",
                      background: !emailLooksValid || otpSending ? "#fcfbfe" : "#e6e1f0",
                      color: "#1b1530",
                      fontSize: 13,
                      cursor: !emailLooksValid || otpSending ? "not-allowed" : "pointer",
                      opacity: !emailLooksValid || otpSending ? 0.6 : 1,
                    }}
                  >
                    {otpSending ? t("forgot.sending") : t("reg.verify_email")}
                  </button>
                )}
              </div>
              <span style={{ display: "block", marginTop: 4, fontSize: 11, color: "#8c8595" }}>
                {emailIsVerified
                  ? t("reg.email_used_for_sign_in")
                  : t("reg.email_will_verify")}
              </span>
            </label>
            <label style={labelStyle}>
              {t("reg.portal_password")}
              <input
                required
                type="password"
                autoComplete="new-password"
                value={form.password}
                onChange={(e) => update("password", e.target.value)}
                placeholder={t("reg.password_placeholder")}
                style={{ ...inputStyle, marginTop: 6 }}
              />
            </label>
            <label style={labelStyle}>
              {t("reg.confirm_password")}
              <input
                required
                type="password"
                autoComplete="new-password"
                value={form.confirmPassword}
                onChange={(e) => update("confirmPassword", e.target.value)}
                placeholder={t("reg.reenter_password")}
                style={{ ...inputStyle, marginTop: 6 }}
              />
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <label style={labelStyle}>
                {t("reg.tin_number")}
                <input
                  required
                  value={form.tin}
                  onChange={(e) => update("tin", e.target.value)}
                  placeholder={t("reg.tin_placeholder")}
                  style={{ ...inputStyle, marginTop: 6 }}
                />
              </label>
              <label style={labelStyle}>
                {t("reg.registration_number")}
                <input
                  required
                  value={form.registrationNumber}
                  onChange={(e) => update("registrationNumber", e.target.value)}
                  placeholder={t("reg.registration_placeholder")}
                  style={{ ...inputStyle, marginTop: 6 }}
                />
              </label>
            </div>
            <label style={labelStyle}>
              {t("reg.tin_pdf")}
              <input
                required
                type="file"
                accept="application/pdf,.pdf"
                style={{ ...inputStyle, marginTop: 6, padding: 8 }}
                onChange={async (e) => {
                  try {
                    const f = e.target.files?.[0];
                    setError("");
                    setTinPdfFile(null);
                    if (!f) {
                      e.target.value = "";
                      return;
                    }
                    const v = await validateMerchantCompliancePdfFile(f);
                    if (!v.ok) {
                      setError(v.message);
                      e.target.value = "";
                      return;
                    }
                    setTinPdfFile(f);
                  } catch (err) {
                    setError(friendlyError(err, t("reg.pdf_invalid")));
                    e.target.value = "";
                  }
                }}
              />
              <span style={{ display: "block", marginTop: 4, fontSize: 11, color: "#8c8595" }}>
                {t("reg.tin_pdf_hint")}
              </span>
              {tinPdfFile ? (
                <span style={{ display: "block", marginTop: 6, fontSize: 12, color: "#6b4eaa", fontWeight: 600 }}>
                  {t("reg.selected", { name: tinPdfFile.name })}
                </span>
              ) : null}
            </label>
            <label style={labelStyle}>
              {t("reg.registration_pdf")}
              <input
                required
                type="file"
                accept="application/pdf,.pdf"
                style={{ ...inputStyle, marginTop: 6, padding: 8 }}
                onChange={async (e) => {
                  try {
                    const f = e.target.files?.[0];
                    setError("");
                    setBusinessRegistrationPdfFile(null);
                    if (!f) {
                      e.target.value = "";
                      return;
                    }
                    const v = await validateMerchantCompliancePdfFile(f);
                    if (!v.ok) {
                      setError(v.message);
                      e.target.value = "";
                      return;
                    }
                    setBusinessRegistrationPdfFile(f);
                  } catch (err) {
                    setError(friendlyError(err, t("reg.pdf_invalid")));
                    e.target.value = "";
                  }
                }}
              />
              <span style={{ display: "block", marginTop: 4, fontSize: 11, color: "#8c8595" }}>
                {t("reg.registration_pdf_hint")}
              </span>
              {businessRegistrationPdfFile ? (
                <span style={{ display: "block", marginTop: 6, fontSize: 12, color: "#6b4eaa", fontWeight: 600 }}>
                  {t("reg.selected", { name: businessRegistrationPdfFile.name })}
                </span>
              ) : null}
            </label>
            <label style={labelStyle}>
              {t("reg.license_pdf")}
              <input
                required
                type="file"
                accept="application/pdf,.pdf"
                style={{ ...inputStyle, marginTop: 6, padding: 8 }}
                onChange={async (e) => {
                  try {
                    const f = e.target.files?.[0];
                    setError("");
                    setBusinessLicensePdfFile(null);
                    if (!f) {
                      e.target.value = "";
                      return;
                    }
                    const v = await validateMerchantCompliancePdfFile(f);
                    if (!v.ok) {
                      setError(v.message);
                      e.target.value = "";
                      return;
                    }
                    setBusinessLicensePdfFile(f);
                  } catch (err) {
                    setError(friendlyError(err, t("reg.pdf_invalid")));
                    e.target.value = "";
                  }
                }}
              />
              <span style={{ display: "block", marginTop: 4, fontSize: 11, color: "#8c8595" }}>
                {t("reg.license_pdf_hint")}
              </span>
              {businessLicensePdfFile ? (
                <span style={{ display: "block", marginTop: 6, fontSize: 12, color: "#6b4eaa", fontWeight: 600 }}>
                  {t("reg.selected", { name: businessLicensePdfFile.name })}
                </span>
              ) : null}
            </label>
            <fieldset style={{ border: "none", padding: 0, margin: 0 }}>
              <legend style={{ ...labelStyle, padding: 0 }}>Store segments *</legend>
              <MerchantCategoryPicker
                hideTitle
                theme="register"
                idPrefix="reg-cat"
                options={categoryOptions}
                selected={selectedCategories}
                onToggle={toggleCategory}
              />
            </fieldset>
            <label style={labelStyle}>
              {t("reg.storefront_photo")}
              <input
                required
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,.heic,.heif"
                style={{ ...inputStyle, marginTop: 6, padding: 8 }}
                onChange={async (e) => {
                  try {
                    const f = e.target.files?.[0];
                    setError("");
                    setProfilePreviewUrl((prev) => {
                      if (prev) URL.revokeObjectURL(prev);
                      return null;
                    });
                    setProfileImageFile(null);
                    if (!f) {
                      e.target.value = "";
                      return;
                    }
                    const r = await createValidatedImageObjectUrl(f, { maxBytes: 5 * 1024 * 1024 });
                    if (r.error) {
                      setError(r.error);
                      e.target.value = "";
                      return;
                    }
                    setProfileImageFile(f);
                    setProfilePreviewUrl(r.url);
                  } catch (err) {
                    setError(friendlyError(err, t("reg.image_failed")));
                    e.target.value = "";
                  }
                }}
              />
              <span style={{ display: "block", marginTop: 4, fontSize: 11, color: "#8c8595" }}>
                {t("reg.storefront_hint")}
              </span>
              {profilePreviewUrl ? (
                <img
                  alt={t("reg.preview")}
                  src={profilePreviewUrl}
                  style={{ marginTop: 10, maxHeight: 96, maxWidth: "100%", borderRadius: 14, objectFit: "cover" }}
                />
              ) : null}
            </label>
            <label style={labelStyle}>
              {t("reg.office_address")}
              <input
                required
                value={form.officeAddress}
                onChange={(e) => update("officeAddress", e.target.value)}
                style={{ ...inputStyle, marginTop: 6 }}
              />
            </label>
            <div style={{ marginTop: 4 }}>
              <span style={{ ...labelStyle, display: "block" }}>Pick office location on map</span>
              <OfficeLocationPickerMap
                latitude={form.officeLat}
                longitude={form.officeLng}
                onLocationChange={(officeLat, officeLng) => setForm((prev) => ({ ...prev, officeLat, officeLng }))}
              />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <label style={labelStyle}>
                {t("reg.office_latitude")}
                <input
                  required
                  inputMode="decimal"
                  value={form.officeLat}
                  onChange={(e) => update("officeLat", e.target.value)}
                  placeholder={t("reg.lat_placeholder")}
                  style={{ ...inputStyle, marginTop: 6 }}
                />
              </label>
              <label style={labelStyle}>
                {t("reg.office_longitude")}
                <input
                  required
                  inputMode="decimal"
                  value={form.officeLng}
                  onChange={(e) => update("officeLng", e.target.value)}
                  placeholder={t("reg.lng_placeholder")}
                  style={{ ...inputStyle, marginTop: 6 }}
                />
              </label>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <label style={labelStyle}>
                {t("reg.contact_person")}
                <input required value={form.contactPerson} onChange={(e) => update("contactPerson", e.target.value)} style={{ ...inputStyle, marginTop: 6 }} />
              </label>
              <label style={labelStyle}>
                {t("reg.contact_phone")}
                <input
                  required
                  type="tel"
                  value={form.contactPhone}
                  onChange={(e) => update("contactPhone", e.target.value)}
                  style={{ ...inputStyle, marginTop: 6 }}
                />
              </label>
            </div>
          </div>
          {error ? (
            <div
              style={{
                marginTop: 16,
                padding: "10px 12px",
                backgroundColor: "#fbefec",
                color: "#a63a3a",
                borderRadius: 12,
                border: "1px solid #e3bdb6",
                fontSize: 14,
              }}
            >
              {error}
            </div>
          ) : null}
          <button
            type="submit"
            className="primary"
            disabled={submitting || !emailIsVerified}
            title={!emailIsVerified ? t("reg.verify_first") : undefined}
            style={{
              marginTop: 22,
              width: "100%",
              padding: "12px 16px",
              color: "#ffffff",
              border: "none",
              borderRadius: 12,
              cursor: submitting || !emailIsVerified ? "not-allowed" : "pointer",
              fontSize: 15,
              fontWeight: 500,
              opacity: submitting || !emailIsVerified ? 0.7 : 1,
            }}
          >
            {submitting ? t("reg.submitting") : t("reg.create_account")}
          </button>
        </form>
        <p style={{ marginTop: 12, fontSize: 14, color: "#6a6480", textAlign: "center" }}>
          {t("reg.already_have_access")}{" "}
          <Link to="/login" style={{ color: "#6b4eaa", fontWeight: 600 }}>
            {t("login.sign_in")}
          </Link>
        </p>
      </AuthShell>
      {otpModalOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="email-otp-title"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            background: "rgba(46, 33, 84, 0.14)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onClick={(ev) => {
            if (ev.target === ev.currentTarget) closeOtpModal();
          }}
          onKeyDown={(ev) => {
            if (ev.key === "Escape") closeOtpModal();
          }}
        >
          <div className="auth-card" style={{ margin: 0, maxWidth: 400, padding: 28 }} onClick={(ev) => ev.stopPropagation()}>
            <h3 id="email-otp-title" style={{ marginTop: 0, marginBottom: 4, fontSize: 18 }}>
              {t("reg.verify_your_email")}
            </h3>
            <p style={{ margin: "0 0 16px 0", fontSize: 13, color: "#6a6480" }}>
              {t("reg.enter_code_sent_to", { email: normalizedEmail })}
            </p>
            <form onSubmit={handleConfirmOtp}>
              <label style={labelStyle}>
                {t("forgot.code_label")}
                <input
                  required
                  autoFocus
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={otpValue}
                  onChange={(e) => setOtpValue(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123456"
                  style={{ ...inputStyle, marginTop: 6, letterSpacing: 4, fontSize: 18, textAlign: "center" }}
                />
              </label>
              {otpError ? (
                <div style={{ marginTop: 12, fontSize: 13, color: "#a63a3a" }}>{otpError}</div>
              ) : null}
              <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
                <button
                  type="button"
                  onClick={closeOtpModal}
                  style={{
                    flex: 1,
                    padding: "10px 12px",
                    borderRadius: 12,
                    border: "1px solid #e6e1f0",
                    background: "transparent",
                    color: "#6a6480",
                    cursor: "pointer",
                    fontSize: 14,
                  }}
                >
                  {t("common.cancel")}
                </button>
                <button
                  type="submit"
                  className="primary"
                  disabled={otpVerifying || otpValue.length !== 6}
                  style={{
                    flex: 1,
                    padding: "10px 12px",
                    borderRadius: 12,
                    border: "none",
                    color: "#ffffff",
                    cursor: otpVerifying || otpValue.length !== 6 ? "not-allowed" : "pointer",
                    opacity: otpVerifying || otpValue.length !== 6 ? 0.7 : 1,
                    fontSize: 14,
                  }}
                >
                  {otpVerifying ? t("reg.confirming") : t("reg.confirm")}
                </button>
              </div>
              <button
                type="button"
                onClick={handleSendOtp}
                disabled={otpSending}
                style={{
                  marginTop: 12,
                  width: "100%",
                  padding: "6px",
                  background: "transparent",
                  border: "none",
                  color: "#6b4eaa", fontWeight: 600,
                  fontSize: 12,
                  cursor: otpSending ? "not-allowed" : "pointer",
                }}
              >
                {otpSending ? t("reg.resending") : t("reg.resend_code")}
              </button>
              {otpNotice ? (
                <div style={{ marginTop: 8, fontSize: 12, color: "#8c8595", textAlign: "center" }}>{otpNotice}</div>
              ) : null}
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
}
